/**
 * Pantalla de **precios del cotizador de puertas** (`/admin/precios-cotizador`).
 * Reemplaza el placeholder `ROUTES.cotizadorPrecios`. Equivale a
 * `admin_precios_puertas.html` del SSR.
 *
 * Solo-admin (permiso `all`, mismo que la API `@api_require_permission("all")` y
 * el SSR `@require_admin`) vía `RequireAdmin`. Orquesta:
 *  - la lectura de la config (`usePrecios`) con estados de carga/error;
 *  - el formulario (`PreciosForm`) y el guardado (`useUpdatePrecios`), que en
 *    éxito **invalida** la query para recargar los valores persistidos (paridad
 *    con el "guardar + recargar" del SSR).
 *
 * Los precios son **globales** (sin sede): la query no se segmenta por sede.
 * Presentacional + hooks; sin `fetch`.
 */
import { adminErrorMessage } from "../format";
import { usePrecios, useUpdatePrecios } from "../hooks/usePrecios";
import type { UpdatePreciosBody } from "../precios.types";
import { PreciosForm } from "./PreciosForm";
import { RequireAdmin } from "./RequireAdmin";

export function PreciosScreen() {
  const preciosQuery = usePrecios();
  const updatePrecios = useUpdatePrecios();

  function handleSubmit(body: UpdatePreciosBody) {
    updatePrecios.mutate(body);
  }

  return (
    <RequireAdmin
      title="Precios cotizador"
      deniedMessage="No tienes permiso para gestionar los precios del cotizador."
    >
      <div className="flex flex-col gap-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">
            Precios del cotizador
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Configuración global de precios del cotizador de puertas (lámina,
            doblez, perfiles, tablas por medida).
          </p>
        </div>

        {preciosQuery.isPending ? (
          <p className="py-8 text-center text-sm text-slate-500" role="status">
            Cargando precios…
          </p>
        ) : preciosQuery.isError ? (
          <p className="py-8 text-center text-sm text-red-600" role="alert">
            {adminErrorMessage(preciosQuery.error)}
          </p>
        ) : (
          <PreciosForm
            config={preciosQuery.data}
            isSubmitting={updatePrecios.isPending}
            isSaved={updatePrecios.isSuccess}
            apiError={updatePrecios.isError ? updatePrecios.error : null}
            onSubmit={handleSubmit}
          />
        )}
      </div>
    </RequireAdmin>
  );
}
